/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { produce } from "immer";
import { noop } from "lodash";
import { useCallback, useRef } from "react";
import { useUpdateEffect } from "react-use";
import { generateGuid } from "utils/empty-exists";
import { dispatch } from "../../../data/store";
import { setLoadingForm } from "../../../data/store/reducers/form";
// import { Logger } from "cms/utils/log/logger";

/**
 * @template {PositionalItem} T
 * @param {T[]} items
 * @param {SetState<PersistableList<T>>} setItems
 * @param {keyof T} idField
 * @param {OnDragSwap<T>=} onSwap
 * @returns {SortableMethods<T>}
 */
const useDragAndDropMethods = (items, setItems, idField, onSwap = noop) => {
    /**
     * When hovering, we're locked into a closure.
     * 
     * This ref allows us to get the latest information since an object will
     * contain the same reference.
     */
    const latestItems = useRef(items);

    // when items change, update the ref
    useUpdateEffect(() => {
        latestItems.current = items;
    }, [items]);

    /**
     * @param {string} id
     */
    const findItem = useCallback((id) => {
        const items = latestItems.current;
        const index = latestItems.current.findIndex(
            (item) => item[idField] === id,
        );

        return {
            item: items[index],
            index,
        };
    }, [idField, latestItems]);

    /**
     * @param {string} id
     * @param {number} targetIndex
     */
    const swapItems = useCallback((id, targetIndex) => {
        const { item, index: sourceIndex } = findItem(id);

        if (!item) {
            return;
        }

        console.debug(`[swap-items] Swapping item indices ${sourceIndex} and ${targetIndex}`);
        setItems(
            produce((draft) => {
                const sourceItem = draft.list[sourceIndex];
                const targetItem = draft.list[targetIndex];

                // inline swap
                draft.list[sourceIndex] = draft.list.splice(targetIndex, 1, sourceItem,)[0];

                // adjust swap positions (as 1-based) if they exist
                if (typeof sourceItem.position === "number") {
                    sourceItem.position = targetIndex + 1;
                    targetItem.position = sourceIndex + 1;
                }

                onSwap(draft, sourceItem, targetItem);
            }),
        );
    }, [findItem, onSwap, setItems]);

    const onDrop = useCallback(() => {
        setItems(
            produce((draft) => {
                draft.persist = generateGuid();
            })
        );
    }, [setItems]);

    return {
        findItem,
        swapItems,
        onDrop,
    };
};

export { useDragAndDropMethods };
